// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import {
  Accordion,
  Box,
  CheckIcon,
  Flex,
  PercentageIcon,
  ProfileCircledIcon,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';

import { useGetStore } from '@jane/shared/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';

import { selectCart } from '../../../../customer/redux/cart';
import { selectCustomer } from '../../../../customer/redux/customer';
import { useCustomerSelector } from '../../../../customer/selectors';
import {
  applicableCartTotalSpecial,
  applicableProductSpecials,
} from '../../../../lib/cart';
import { Divider } from '../cartDrawer/cartDrawer.styles';
import { SpecialsWrapper } from './cartDrawerSpecials.styles';
import { CustomerLoyaltyPoints } from './customerLoyaltyPoints/customerLoyaltyPoints';
import { CustomerLoyaltyPointsGuest } from './customerLoyaltyPoints/customerLoyaltyPointsGuest';
import {
  GroupDiscounts,
  groupSpecialsTitle,
} from './groupDiscounts/groupDiscounts';
import { PromoCodes } from './promoCodes/promoCodes';

interface CartDrawerSpecialsProps {
  headless?: boolean;
  specials: StoreSpecial[];
}

export const BoxWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.colors.grays.white,
  borderRadius: '25px',
  border: `1px solid ${theme.colors.grays.light}`,
  ...spacing({
    p: 56,
    mt: 24,
    mb: 12,
  }),
}));

export const CartDrawerSpecials = ({
  specials,
  headless,
}: CartDrawerSpecialsProps) => {
  const isMobile = useMobileMediaQuery({});

  const { cart, queryPromoCode } = useCustomerSelector(selectCart);
  const { id: customerId, phone: customerPhone } =
    useCustomerSelector(selectCustomer);
  const { data: store } = useGetStore(cart?.store?.id);

  const {
    cart_discount_special_id,
    discounted_subtotal,
    reservation_mode,
    loyalty_points_stacking_enabled,
  } = cart;

  const specialsLabel = store?.store_compliance_language_settings?.['specials'];
  const productSpecials = applicableProductSpecials(cart, specials);

  const groupSpecials = specials.filter(
    (special) =>
      special.special_type === 'qualified_group' &&
      special.reservation_modes &&
      special.reservation_modes[reservation_mode]
  );

  const cartTotalSpecial = applicableCartTotalSpecial(
    specials,
    cart_discount_special_id
  );
  const appliedSpecials = cartTotalSpecial
    ? productSpecials.concat([cartTotalSpecial])
    : productSpecials;

  const appliedPromoCodeSpecial = appliedSpecials.find((s) => s.promo_code);

  const appliedGroupSpecial = appliedSpecials.find(
    (s) => s.special_type === 'qualified_group'
  );

  const shouldDisplayLoyaltyPoints = () => {
    return Boolean(customerPhone && store?.crm_integration && !!customerId);
  };

  return (
    <SpecialsWrapper flexDirection="column" headless={headless}>
      {headless ? (
        <BoxWrapper>
          <Typography branded variant="header-bold" mb={24}>
            Apply{' '}
            {store?.store_compliance_language_settings?.['promo_code'] ||
              'promo code'}
          </Typography>
          <PromoCodes
            headless={headless}
            reservationMode={reservation_mode}
            userGroupSpecialId={appliedGroupSpecial?.id}
          />
        </BoxWrapper>
      ) : (
        <Accordion.Item id="promo-code" defaultExpanded={!!queryPromoCode}>
          <Accordion.Heading px={isMobile ? 24 : 40} py={20}>
            <Flex alignItems="center">
              <Flex flexDirection="column">
                <Flex>
                  <div>
                    <PercentageIcon />
                  </div>
                  <Typography variant="body-bold" ml={16}>
                    Apply{' '}
                    {store?.store_compliance_language_settings?.[
                      'promo_code'
                    ] || 'promo code'}
                  </Typography>
                </Flex>
                {appliedPromoCodeSpecial && (
                  <Flex alignItems="center" mt={8}>
                    <CheckIcon size="sm" color="success" mr={8} />
                    <Typography>{appliedPromoCodeSpecial.title}</Typography>
                  </Flex>
                )}
              </Flex>
              <Flex ml="auto">
                <Accordion.Indicator iconSize="md" />
              </Flex>
            </Flex>
          </Accordion.Heading>
          <Accordion.Content px={isMobile ? 24 : 40} pb={24} destroyOnClose>
            <PromoCodes
              reservationMode={reservation_mode}
              userGroupSpecialId={appliedGroupSpecial?.id}
            />
          </Accordion.Content>
        </Accordion.Item>
      )}
      {!!customerId && groupSpecials.length > 0 && headless && (
        <BoxWrapper>
          <Typography
            variant="header-bold"
            branded
            mb={24}
            // eslint-disable-next-line react/forbid-component-props -- legacy emotion
            css={{ wordBreak: 'break-word' }}
          >
            {groupSpecialsTitle(groupSpecials, specialsLabel)}
          </Typography>
          <GroupDiscounts
            userGroupSpecialId={appliedGroupSpecial?.id}
            headless={headless}
            groupSpecials={groupSpecials}
          />
          {appliedGroupSpecial && (
            <Flex alignItems="center" mt={8}>
              <CheckIcon size="sm" color="success" mr={8} />
              <Typography>{appliedGroupSpecial.title}</Typography>
            </Flex>
          )}
        </BoxWrapper>
      )}
      {!!customerId && groupSpecials.length > 0 && !headless && (
        <>
          <Divider />
          <Accordion.Item id="group-discount">
            <Accordion.Heading px={isMobile ? 24 : 40} py={20}>
              <Flex alignItems="center" justifyContent="space-between">
                <Flex flexDirection="column">
                  <Flex alignItems="center">
                    <div>
                      <ProfileCircledIcon />
                    </div>
                    <Typography
                      variant="body-bold"
                      ml={16}
                      // eslint-disable-next-line react/forbid-component-props -- legacy emotion
                      css={{ wordBreak: 'break-word' }}
                    >
                      {groupSpecialsTitle(groupSpecials, specialsLabel)}
                    </Typography>
                  </Flex>
                  {appliedGroupSpecial && (
                    <Flex alignItems="center" mt={8}>
                      <CheckIcon size="sm" color="success" mr={8} />
                      <Typography>{appliedGroupSpecial.title}</Typography>
                    </Flex>
                  )}
                </Flex>
                <Flex pl={16}>
                  <Accordion.Indicator iconSize="md" />
                </Flex>
              </Flex>
            </Accordion.Heading>
            <Accordion.Content px={isMobile ? 24 : 40} destroyOnClose>
              <GroupDiscounts
                userGroupSpecialId={appliedGroupSpecial?.id}
                groupSpecials={groupSpecials}
              />
            </Accordion.Content>
          </Accordion.Item>
        </>
      )}

      {!!store?.crm_integration && !shouldDisplayLoyaltyPoints() && (
        <>
          {!headless && <Divider />}
          <CustomerLoyaltyPointsGuest
            isHeadless={headless}
            signedIn={!!customerId}
            storeRewardLabel={
              store?.store_compliance_language_settings?.['store_reward']
            }
          />
        </>
      )}

      {shouldDisplayLoyaltyPoints() && !headless && (
        <>
          <Divider />
          <CustomerLoyaltyPoints
            cartUuid={cart.uuid}
            phone={customerPhone}
            loyaltyPointsStacking={loyalty_points_stacking_enabled}
            postDiscountSubtotal={discounted_subtotal}
            appliedRedemptions={cart.crm_redemptions}
            reservationMode={reservation_mode}
            store={store}
          />
        </>
      )}

      {shouldDisplayLoyaltyPoints() && headless && (
        <BoxWrapper>
          <Typography
            variant="header-bold"
            branded
            mb={24}
            // eslint-disable-next-line react/forbid-component-props -- legacy emotion
            css={{ wordBreak: 'break-word' }}
          >
            Use store rewards
          </Typography>
          <CustomerLoyaltyPoints
            headless={true}
            cartUuid={cart.uuid}
            phone={customerPhone}
            loyaltyPointsStacking={loyalty_points_stacking_enabled}
            postDiscountSubtotal={discounted_subtotal}
            appliedRedemptions={cart.crm_redemptions}
            reservationMode={reservation_mode}
            store={store}
          />
        </BoxWrapper>
      )}
    </SpecialsWrapper>
  );
};
